import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getComponentById, deleteComponent } from '../api/componentService';
import CodePreviewPanel from '../components/molecules/CodePreviewPanel';
import Button from '../components/atoms/Button';
import Text from '../components/atoms/Text';
import Badge from '../components/atoms/Badge';
import Tag from '../components/atoms/Tag';
import CodeInput from '../components/atoms/CodeInput';
import TabButton from '../components/atoms/TabButton';
import { getUserRole, getUserId } from '../utils/auth';
import { useToast } from '../components/atoms/Toast';

/**
 * Component Viewer Page
 * Displays component details with read-only code view and interactive preview
 */
const ComponentViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Component state
  const [component, setComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCodeTab, setActiveCodeTab] = useState('html');
  const toast = useToast();

  // Load component data
  const loadComponent = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getComponentById(id);
      setComponent(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load component. Please try again.');
      console.error('Error loading component:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadComponent();
  }, [loadComponent]);

  const handleEdit = () => {
    navigate(`/components/edit/${id}`);
  };

  const handleDelete = async () => {
    const ok = await (toast?.confirm ? toast.confirm('Delete this component? This action cannot be undone.') : Promise.resolve(window.confirm('Delete this component? This action cannot be undone.')));
    if (!ok) return;
    try {
      await deleteComponent(id);
      toast?.success?.('Component deleted');
      navigate('/components');
    } catch (err) {
      setError('Failed to delete component. Please try again.');
      toast?.error?.('Failed to delete component');
      console.error('Error deleting component:', err);
    }
  };

  const handleBack = () => {
    navigate('/components');
  };

  // Admin check
  const isAdmin = getUserRole() === 'ADMIN';
  const currentUserId = getUserId();
  const isOwnerPrivate = component?.visibility === 'private' && component?.creatorId && component.creatorId === currentUserId;

  // Codetabs 
  const codeTabs = [
    {
      id: 'html',
      label: 'HTML',
      value: component?.htmlCode || '',
      language: 'html',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'css',
      label: 'CSS',
      value: component?.cssCode || '',
      language: 'css',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17v4a2 2 0 002 2h4" />
        </svg>
      )
    },
    {
      id: 'js',
      label: 'JavaScript',
      value: component?.jsCode || '',
      language: 'javascript',
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      )
    }
  ];
  // activetabData 
  const activeTabData = codeTabs.find(tab => tab.id === activeCodeTab);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <Text>Loading component...</Text>
        </div>
      </div>
    );
  }

  if (error || !component) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Text variant="h3" className="text-gray-900 mb-4">
            {error || 'Component not found'}
          </Text>
          <Button onClick={handleBack}>
            Back to Components
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="default"
                onClick={handleBack}
                leftIcon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                }
              >
                Back
              </Button>
              <div>
                <Text variant="h1" className="text-gray-900">
                  {component.title}
                </Text>
                {component.description && (
                  <Text variant="body" className="text-gray-600 mt-1">
                    {component.description}
                  </Text>
                )}
              </div>
            </div>
            
            {(isAdmin || isOwnerPrivate) && (
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  onClick={handleEdit}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  }
                >
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDelete}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  }
                >
                  Delete
                </Button>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="mt-4 flex items-center gap-4 flex-wrap">
            {component.category && (
              <Badge variant="purple" size="md">
                {component.category}
              </Badge>
            )}
            
            {component.tags && component.tags.length > 0 && (
              <div className="flex gap-2">
                {component.tags.map((tag, index) => (
                  <Tag key={index}>{tag}</Tag>
                ))}
              </div>
            )}
            
            <Text variant="small" className="text-gray-500">
              Updated {new Date(component.updatedAt || Date.now()).toLocaleDateString()}
            </Text>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Live Preview */}
          <div className="space-y-6">
            <CodePreviewPanel
              htmlCode={component.htmlCode}
              cssCode={component.cssCode}
              jsCode={component.jsCode}
              title="Live Preview"
            />
          </div>

          {/* Right Column - Code View */}
          <div className="space-y-6">
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Code Tab Navigation */}
              <div className="border-b border-gray-200 bg-gray-50">
                <div className="flex">
                  {codeTabs.map((tab) => (
                    <TabButton
                      key={tab.id}
                      active={activeCodeTab === tab.id}
                      onClick={() => setActiveCodeTab(tab.id)}
                      icon={tab.icon}
                      className="rounded-none border-b-0"
                    >
                      {tab.label}
                    </TabButton>
                  ))}
                </div>
              </div>

              {/* Code Display */}
              <div className="p-4">
                <div className="mb-2 flex justify-between items-center">
                  <Text variant="h4" className="text-gray-900">
                    {activeTabData.label} Code
                  </Text>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={async () => { await navigator.clipboard.writeText(activeTabData.value); toast?.success?.('Copied to clipboard'); }}
                    leftIcon={
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    }
                  >
                    Copy
                  </Button>
                </div>
                
                <CodeInput
                  value={activeTabData.value}
                  onChange={() => {}} // Read-only
                  placeholder={`No ${activeTabData.label.toLowerCase()} code`}
                  language={activeTabData.language}
                  rows={15}
                  disabled
                  className="min-h-[400px]"
                />
              </div>

              {/* Code Statistics */}
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
                <span>
                  {activeTabData.value ? `${activeTabData.value.length} characters` : 'No code'}
                </span>
                <span>
                  {activeTabData.value ? `${activeTabData.value.split('\n').length} lines` : '0 lines'}
                </span>
              </div>
            </div>

            {/* Component Info */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Text variant="h3" className="text-gray-900 mb-4">
                Component Information
              </Text>
              
              <div className="space-y-3">
                <div>
                  <Text variant="small" className="text-gray-500 mb-1">
                    Created
                  </Text>
                  <Text variant="body">
                    {new Date(component.createdAt || Date.now()).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                </div>
                
                <div>
                  <Text variant="small" className="text-gray-500 mb-1">
                    Last Modified
                  </Text>
                  <Text variant="body">
                    {new Date(component.updatedAt || Date.now()).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Text>
                </div>
                
                <div>
                  <Text variant="small" className="text-gray-500 mb-1">
                    Component ID
                  </Text>
                  <Text variant="small" className="font-mono">
                    {component.id}
                  </Text>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComponentViewer;
